import React, { useState } from 'react';
import { User, WorkOrder, Environment } from '../types';
import { UpgradeRequired } from './ui/UpgradeRequired';
import { calculateEnvironmentSchedule, calculateOrderEndDate } from '../utils/scheduleCalculator';
import { PageHeader } from './ui/PageHeader';
import { FilterBar } from './ui/FilterBar';
import { getStatusColor, getStatusLabel } from '../services/mockData';
import { 
  CalendarDays, 
  ChevronLeft, 
  ChevronRight, 
  Filter, 
  Clock, 
  User as UserIcon, 
  MapPin, 
  X, 
  Activity, 
  CheckCircle,
  FileText,
  Calendar
} from 'lucide-react';

interface AgendaMontagemProps {
  currentUser: User;
  orders: WorkOrder[];
  currentCompany: any;
  onSelectOrder?: (order: WorkOrder) => void;
  onChangeView: (view: string) => void;
  allUsers: User[];
}

const UpgradeLock = ({ onUpgrade }: { onUpgrade: () => void }) => (
  <div style={{
    padding: '16px 14px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 10,
  }}>
    <div style={{
      width: 32, height: 32, borderRadius: 8,
      background: 'var(--blue-dim)',
      border: '1px solid var(--blue-border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width="14" height="14" fill="none" stroke="var(--blue)" strokeWidth="2" viewBox="0 0 24 24">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    </div>
    <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>
      Disponível nos planos <strong style={{ color: 'var(--blue)' }}>Pro</strong> e{' '}
      <strong style={{ color: 'var(--purple)' }}>Business</strong>
    </p>
    <button
      onClick={onUpgrade}
      style={{
        padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500,
        cursor: 'pointer', border: 'none',
        background: 'var(--blue)', color: '#fff',
        display: 'inline-flex', alignItems: 'center', gap: 5,
      }}
    >
      <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
        <polyline points="17 6 23 6 23 12"/>
      </svg>
      Ver planos
    </button>
  </div>
);

export const AgendaMontagem: React.FC<AgendaMontagemProps> = ({ 
  currentUser, 
  orders, 
  currentCompany, 
  onSelectOrder,
  onChangeView,
  allUsers
}) => {
  const [filterTecnico, setFilterTecnico] = useState('todos');
  const [filterTipo, setFilterTipo] = useState('todos'); // 'todos' | 'montagem' | 'assistencia'
  const [filterStatus, setFilterStatus] = useState('todos'); // 'todos' | 'em-andamento' | 'agendada' | 'concluida'
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Modal states
  const [selectedOrderForModal, setSelectedOrderForModal] = useState<WorkOrder | null>(null);
  const [selectedDayOrders, setSelectedDayOrders] = useState<WorkOrder[] | null>(null);
  const [selectedDayDate, setSelectedDayDate] = useState<Date | null>(null);

  const [activeTab, setActiveTab] = useState<'calendario' | 'disponibilidade'>('calendario');
  const [expandedTech, setExpandedTech] = useState<string | null>(null);

  const isStarter = currentCompany?.plano?.toLowerCase() === 'starter';
  const isPro = ['pro', 'business'].includes(
    currentCompany?.plano?.toLowerCase()
  );

  // 1. Filter orders based on user role, plan, and selected filters
  const filteredOrders = orders.filter(order => {
    if (isStarter) {
      // Starter vê apenas O.S. onde é técnico responsável
      if (currentUser.role === 'TECHNICIAN') {
        return order.technicianId === currentUser.id || 
          order.environments?.some(e => e.technicianId === currentUser.id);
      }
    }
    
    // Filtro de técnico (Pro/Business)
    if (!isStarter && filterTecnico !== 'todos') {
      const hasTech = order.technicianId === filterTecnico ||
        order.environments?.some(e => e.technicianId === filterTecnico);
      if (!hasTech) return false;
    }
    
    // Filtro de tipo
    if (filterTipo !== 'todos') {
      const typeLower = order.type?.toLowerCase();
      const filterLower = filterTipo === 'montagem' ? 'assembly' : 'assistance';
      if (typeLower !== filterLower) return false;
    }
    
    // Filtro de status
    if (filterStatus !== 'todos') {
      const statusMap: Record<string, string[]> = {
        'em-andamento': ['IN_PROGRESS'],
        'agendada': ['OPEN', 'ASSIGNED'],
        'concluida': ['COMPLETED'],
      };
      if (!statusMap[filterStatus]?.includes(order.status)) return false;
    }
    return true;
  });

  // Extract unique technicians from current orders for Pro/Business filters
  const techniciansMap = new Map<string, string>();
  orders.forEach(order => {
    if (order.technicianId && order.technicianName) {
      techniciansMap.set(order.technicianId, order.technicianName);
    }
    order.environments?.forEach(env => {
      if (env.technicianId && env.technicianName) {
        techniciansMap.set(env.technicianId, env.technicianName);
      }
    });
  });
  const techniciansList = Array.from(techniciansMap.entries()).map(([id, name]) => ({ id, name }));

  // Date helper functions
  const isSameDay = (d1: Date, d2: Date) => {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  const isOrderInCurrentMonth = (order: WorkOrder) => {
    if (!order.suggestedDate) return false;
    const orderDate = new Date(order.suggestedDate.includes('T') ? order.suggestedDate : order.suggestedDate + 'T00:00:00');
    return orderDate.getFullYear() === currentMonth.getFullYear() && orderDate.getMonth() === currentMonth.getMonth();
  };

  // Helper to get orders active on a specific day
  const getOrdersForDay = (date: Date): WorkOrder[] => {
    return filteredOrders.filter(order => {
      const start = order.suggestedDate ? new Date(order.suggestedDate.includes('T') ? order.suggestedDate : order.suggestedDate + 'T00:00:00') : null;
      if (!start) return false;

      const end = getOrderEndDate(order);

      const d = new Date(date);
      d.setHours(0,0,0,0);
      start.setHours(0,0,0,0);
      end.setHours(0,0,0,0);

      return d >= start && d <= end;
    });
  };

  // Get start & end dates
  const getOrderEndDate = (order: WorkOrder): Date => {
    // Usar eventos da OS se disponíveis para calcular com atrasos
    const events = (order as any).events || (order as any).workOrderEvents || [];

    if (events.length > 0) {
      const scheduled = calculateEnvironmentSchedule(order, events);
      const endDate = calculateOrderEndDate(scheduled);
      if (endDate) return endDate;
    }

    // Fallback sem atrasos
    if (!order.suggestedDate) return new Date();
    const start = new Date(order.suggestedDate.includes('T') ? order.suggestedDate : order.suggestedDate + 'T00:00:00');
    const maxDays = order.environments?.reduce((max, env) =>
      Math.max(max, env.estimatedDays || 1), 1) || 1;
    const end = new Date(start);
    end.setDate(start.getDate() + maxDays - 1);
    return end;
  };

  // Buscar técnico — direto na OS ou nos ambientes
  const getTechnicianName = (order: WorkOrder): string => {
    // 1. Verificar diretamente na OS
    if (order.technicianName) return order.technicianName;

    // 2. Buscar nos ambientes
    const envWithTech = order.environments?.find(env => env.technicianName);
    if (envWithTech?.technicianName) {
      // Se todos os ambientes têm o mesmo técnico, mostrar só um nome
      const allSameTech = order.environments?.every(
        env => env.technicianName === envWithTech.technicianName
      );
      if (allSameTech) return envWithTech.technicianName;

      // Se ambientes têm técnicos diferentes, listar todos únicos
      const techNames = [...new Set(
        order.environments
          ?.filter(env => env.technicianName)
          .map(env => env.technicianName!)
      )];
      return techNames.join(', ');
    }

    return 'Não atribuído';
  };

  // --- Lógica de disponibilidade ---
  // Período da timeline — 20 dias a partir de hoje
  const today = new Date();
  today.setHours(0,0,0,0);
  const periodStart = new Date(today);
  const periodEnd = new Date(today);
  periodEnd.setDate(today.getDate() + 20);

  // Para cada técnico, buscar suas O.S. ativas e agendadas
  const getTechOrders = (techId: string) => {
    return orders.filter(order =>
      ['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED'].includes(order.status) &&
      order.suggestedDate &&
      (
        order.technicianId === techId ||
        order.environments?.some(e => e.technicianId === techId)
      )
    ).sort((a, b) =>
      new Date(a.suggestedDate!.includes('T') ? a.suggestedDate! : a.suggestedDate! + 'T00:00:00').getTime() -
      new Date(b.suggestedDate!.includes('T') ? b.suggestedDate! : b.suggestedDate! + 'T00:00:00').getTime()
    );
  };

  // Calcular data de término de uma O.S. (objeto Date)
  const getOrderEndDateObj = (order: WorkOrder): Date => {
    return getOrderEndDate(order);
  };

  // Status do técnico
  const getTechStatus = (techId: string): 'disponivel' | 'andamento' | 'agendado' => {
    const techOrders = getTechOrders(techId).filter(o => o.status !== 'COMPLETED');
    if (!techOrders.length) return 'disponivel';
    const hasInProgress = techOrders.some(o => o.status === 'IN_PROGRESS');
    if (hasInProgress) return 'andamento';
    return 'agendado';
  };

  // Próxima data de liberação
  const getNextAvailable = (techId: string): Date | null => {
    const activeOrders = getTechOrders(techId)
      .filter(o => !['COMPLETED', 'CANCELLED'].includes(o.status))
      .sort((a, b) => getOrderEndDateObj(b).getTime() - getOrderEndDateObj(a).getTime());
    if (!activeOrders.length) return null;
    const lastEnd = getOrderEndDateObj(activeOrders[0]);
    const nextDay = new Date(lastEnd);
    nextDay.setDate(lastEnd.getDate() + 1);
    return nextDay;
  };

  // Posição e largura da barra na timeline (%)
  const getBarPosition = (order: WorkOrder) => {
    const start = new Date(order.suggestedDate!.includes('T') ? order.suggestedDate! : order.suggestedDate! + 'T00:00:00');
    const end = getOrderEndDateObj(order);
    const totalDays = (periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24);
    const startOffset = Math.max(0, (start.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24));
    const duration = Math.min(
      (end.getTime() - Math.max(start.getTime(), periodStart.getTime())) / (1000 * 60 * 60 * 24) + 1,
      totalDays - startOffset
    );
    return {
      left: `${(startOffset / totalDays) * 100}%`,
      width: `${Math.max(3, (duration / totalDays) * 100)}%`,
    };
  };

  // Posição do "hoje" na timeline
  const todayPosition = `${((today.getTime() - periodStart.getTime()) / (periodEnd.getTime() - periodStart.getTime())) * 100}%`;

  // Técnicos ordenados: disponíveis por último, em andamento primeiro
  const sortedTechnicians = [...(allUsers || []).filter(u => u.role === 'TECHNICIAN')]
    .sort((a, b) => {
      const statusOrder = { andamento: 0, agendado: 1, disponivel: 2 };
      return statusOrder[getTechStatus(a.id)] - statusOrder[getTechStatus(b.id)];
    });

  // Métricas
  const disponiveisCount = sortedTechnicians.filter(t => getTechStatus(t.id) === 'disponivel').length;
  const andamentoCount = sortedTechnicians.filter(t => getTechStatus(t.id) === 'andamento').length;
  const agendadoCount = sortedTechnicians.filter(t => getTechStatus(t.id) === 'agendado').length;

  const proximaLiberacao = sortedTechnicians
    .filter(t => getTechStatus(t.id) !== 'disponivel')
    .map(t => ({ tech: t, date: getNextAvailable(t.id) }))
    .filter(x => x.date)
    .sort((a, b) => a.date!.getTime() - b.date!.getTime())[0];

  const dateLabels = Array.from({ length: 11 }, (_, i) => {
    const d = new Date(periodStart);
    d.setDate(periodStart.getDate() + i * 2);
    return d.toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit' });
  });

  // Build calendar days (6 weeks = 42 cells)
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of month
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay(); // 0 is Sunday, 1 is Monday...
    
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startOffset);
    
    const days: Date[] = [];
    const tempDate = new Date(startDate);
    for (let i = 0; i < 42; i++) {
      days.push(new Date(tempDate));
      tempDate.setDate(tempDate.getDate() + 1);
    }
    return days;
  };

  const calendarDays = getCalendarDays();
  const monthNameFormatter = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });
  const formattedMonth = monthNameFormatter.format(currentMonth);

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Styles mapping for event status/type
  const getEventStyle = (order: WorkOrder) => {
    if (order.status === 'COMPLETED') {
      return { 
        className: 'event-concluida', 
        color: '#3fb950',
        style: { background: 'rgba(63,185,80,.15)', borderLeft: '3px solid #3fb950', color: '#7ee787', opacity: 0.7 }
      };
    }
    if (order.status === 'IN_PROGRESS') {
      return { 
        className: 'event-andamento', 
        color: '#a371f7',
        style: { background: 'rgba(163,113,247,.2)', borderLeft: '3px solid #a371f7', color: '#c4a0ff' }
      };
    }
    if (order.type === 'ASSEMBLY') {
      return { 
        className: 'event-montagem', 
        color: '#388bfd',
        style: { background: 'rgba(56,139,253,.2)', borderLeft: '3px solid #388bfd', color: '#7bb8ff' }
      };
    }
    return { 
      className: 'event-assistencia', 
      color: '#e3a008',
      style: { background: 'rgba(227,160,8,.2)', borderLeft: '3px solid #e3a008', color: '#f5cc6a' }
    };
  };

  // Sidebar dynamic stats
  const totalMonthAssembly = filteredOrders.filter(o => o.type === 'ASSEMBLY' && isOrderInCurrentMonth(o)).length;
  const totalMonthAssistance = filteredOrders.filter(o => o.type === 'ASSISTANCE' && isOrderInCurrentMonth(o)).length;
  const totalMonthInProgress = filteredOrders.filter(o => o.status === 'IN_PROGRESS' && isOrderInCurrentMonth(o)).length;
  const totalMonthCompleted = filteredOrders.filter(o => o.status === 'COMPLETED' && isOrderInCurrentMonth(o)).length;

  const todayStr = new Date().toISOString().split('T')[0];
  const upcomingOrders = filteredOrders
    .filter(o => o.suggestedDate && o.suggestedDate >= todayStr && o.status !== 'COMPLETED' && o.status !== 'CANCELLED')
    .sort((a, b) => (a.suggestedDate || '').localeCompare(b.suggestedDate || ''))
    .slice(0, 4);

  // Active technicians count
  const activeTechsMap = new Map<string, { name: string; count: number }>();
  filteredOrders.forEach(order => {
    if (isOrderInCurrentMonth(order) && ['OPEN', 'ASSIGNED', 'IN_PROGRESS'].includes(order.status)) {
      if (order.technicianId && order.technicianName) {
        const curr = activeTechsMap.get(order.technicianId) || { name: order.technicianName, count: 0 };
        curr.count += 1;
        activeTechsMap.set(order.technicianId, curr);
      }
      order.environments?.forEach(env => {
        if (env.technicianId && env.technicianName) {
          const curr = activeTechsMap.get(env.technicianId) || { name: env.technicianName, count: 0 };
          curr.count += 1;
          activeTechsMap.set(env.technicianId, curr);
        }
      });
    }
  });
  const activeTechs = Array.from(activeTechsMap.entries()).map(([id, data]) => ({ id, ...data }));

  // Cell click action
  const handleCellClick = (day: Date, dayOrders: WorkOrder[]) => {
    if (dayOrders.length === 0) return;
    if (dayOrders.length === 1) {
      setSelectedOrderForModal(dayOrders[0]);
    } else {
      setSelectedDayOrders(dayOrders);
      setSelectedDayDate(day);
    }
  };

  const handleSelectOrderFromList = (order: WorkOrder) => {
    setSelectedOrderForModal(order);
    setSelectedDayOrders(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agenda de Montagem"
        description="Visualize e planeje as O.S. de montagem e assistência"
        icon={<CalendarDays size={22} style={{ color: 'var(--cyan)' }} />}
      />

      {/* Tab Switcher */}
      <div style={{ display:'flex', gap:2, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:7, padding:3, width:'fit-content', marginBottom:20 }}>
        <button
          onClick={() => setActiveTab('calendario')}
          style={{ padding:'7px 18px', borderRadius:5, fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:7, border: activeTab==='calendario' ? '1px solid var(--border)' : 'none', background: activeTab==='calendario' ? 'var(--bg2)' : 'transparent', color: activeTab==='calendario' ? 'var(--text)' : 'var(--muted)' }}
        >
          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          Calendário
        </button>
        <button
          onClick={() => setActiveTab('disponibilidade')}
          style={{ padding:'7px 18px', borderRadius:5, fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:7, border: activeTab==='disponibilidade' ? '1px solid var(--border)' : 'none', background: activeTab==='disponibilidade' ? 'var(--bg2)' : 'transparent', color: activeTab==='disponibilidade' ? 'var(--text)' : 'var(--muted)' }}
        >
          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          Disponibilidade
        </button>
      </div>

      {activeTab === 'calendario' && (
        <>
          {/* Filters Bar */}
          <FilterBar>
            {!isStarter && (
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                  Técnico
                </label>
                <select
                  value={filterTecnico}
                  onChange={e => setFilterTecnico(e.target.value)}
                  style={{ width: '100%', height: 40, padding: '0 12px', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 8, color: 'var(--sf-white)', fontSize: 13, outline: 'none' }}
                >
                  <option value="todos">Todos os Técnicos</option>
                  {techniciansList.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                Tipo de O.S.
              </label>
              <select
                value={filterTipo}
                onChange={e => setFilterTipo(e.target.value)}
                style={{ width: '100%', height: 40, padding: '0 12px', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 8, color: 'var(--sf-white)', fontSize: 13, outline: 'none' }}
              >
                <option value="todos">Todos os Tipos</option>
                <option value="montagem">Montagem</option>
                <option value="assistencia">Assistência</option>
              </select>
            </div>

            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                Status
              </label>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                style={{ width: '100%', height: 40, padding: '0 12px', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 8, color: 'var(--sf-white)', fontSize: 13, outline: 'none' }}
              >
                <option value="todos">Todos os Status</option>
                <option value="em-andamento">Em Andamento</option>
                <option value="agendada">Agendada</option>
                <option value="concluida">Concluída</option>
              </select>
            </div>
            {/* Legend */}
            <div className="w-full" style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: 'var(--muted)', padding: '12px 4px 0 4px', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#388bfd' }} /> Montagem agendada
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e3a008' }} /> Assistência agendada
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#a371f7' }} /> Em andamento
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3fb950' }} /> Concluída
              </div>
            </div>
          </FilterBar>

          {/* Main Grid Area: Calendar + Sidebar (if Pro/Business) */}
          <div className="flex flex-col lg:flex-row gap-6 items-start w-full">
            
            {/* Calendar Box */}
            <div className="flex-1 w-full min-w-0" style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
              {/* Calendar Header with navigation */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--sf-white)', textTransform: 'capitalize' }}>
                  {formattedMonth}
                </h2>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button 
                    onClick={prevMonth}
                    style={{ padding: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button 
                    onClick={nextMonth}
                    style={{ padding: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderTop: '1px solid var(--border)', borderLeft: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                {/* Week days header */}
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(w => (
                  <div 
                    key={w} 
                    style={{ textAlign: 'center', padding: '10px 0', fontWeight: 'bold', fontSize: 12, background: 'rgba(255,255,255,0.02)', color: 'var(--muted)', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}
                  >
                    {w}
                  </div>
                ))}
                {/* Grid days */}
                {calendarDays.map((day, idx) => {
                  const dayOrders = getOrdersForDay(day);
                  const isCurrentMonthDay = day.getMonth() === currentMonth.getMonth();
                  const isTodayDay = isSameDay(day, new Date());
                  const displayOrders = dayOrders.slice(0, 3);
                  const extraCount = dayOrders.length - 3;

                  return (
                    <div
                      key={idx}
                      onClick={() => handleCellClick(day, dayOrders)}
                      style={{
                        minHeight: 95,
                        padding: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRight: '1px solid var(--border)',
                        borderBottom: '1px solid var(--border)',
                        background: isTodayDay 
                          ? 'rgba(26, 107, 255, 0.05)' 
                          : isCurrentMonthDay 
                            ? 'transparent' 
                            : 'rgba(255,255,255,0.01)',
                        opacity: isCurrentMonthDay ? 1 : 0.35,
                        cursor: dayOrders.length > 0 ? 'pointer' : 'default',
                        transition: 'background 0.2s',
                      }}
                      className="hover:bg-white/[0.02]"
                    >
                      {/* Day Number */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span 
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            width: 22,
                            height: 22,
                            borderRadius: '55%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ...(isTodayDay ? { background: 'var(--blue)', color: '#fff' } : { color: 'var(--sf-white)' })
                          }}
                        >
                          {day.getDate()}
                        </span>
                      </div>

                      {/* Day Events */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                        {displayOrders.map(order => {
                          const est = getEventStyle(order);
                          return (
                            <button
                              key={order.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrderForModal(order);
                              }}
                              style={{
                                ...est.style,
                                padding: '3px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 600,
                                textAlign: 'left',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                width: '100%',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                            >
                              {order.id} · {order.clientName.split(' ')[0]}
                            </button>
                          );
                        })}
                        {extraCount > 0 && (
                          <div 
                            style={{ fontSize: '10px', fontWeight: 700, color: 'var(--cyan)', padding: '2px 4px' }}
                          >
                            + {extraCount} mais
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sidebar Summary */}
            <div className="w-full lg:w-[260px] shrink-0" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              
              {/* Card Resumo do mês */}
              <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, overflow:'hidden', marginBottom:14 }}>
                <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--border)', fontSize:12, fontWeight:600, color:'var(--text)', display:'flex', alignItems:'center', gap:6 }}>
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                  Resumo do mês
                </div>
                {isStarter ? (
                  <UpgradeLock onUpgrade={() => onChangeView('UPGRADE')} />
                ) : (
                  <div style={{ padding:'12px 14px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#388bfd' }}>{totalMonthAssembly}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, fontWeight: 600 }}>Montagens</div>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#e3a008' }}>{totalMonthAssistance}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, fontWeight: 600 }}>Assistências</div>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#a371f7' }}>{totalMonthInProgress}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, fontWeight: 600 }}>Em andamento</div>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#3fb950' }}>{totalMonthCompleted}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, fontWeight: 600 }}>Concluídas</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Card Próximas O.S. */}
              <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, overflow:'hidden', marginBottom:14 }}>
                <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--border)', fontSize:12, fontWeight:600, color:'var(--text)', display:'flex', alignItems:'center', gap:6 }}>
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  Próximas O.S.
                </div>
                {isStarter ? (
                  <UpgradeLock onUpgrade={() => onChangeView('UPGRADE')} />
                ) : (
                  <div style={{ padding:'8px 14px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {upcomingOrders.length === 0 ? (
                        <p style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: '10px 0' }}>Sem próximas O.S. agendadas.</p>
                      ) : (
                        upcomingOrders.map(order => {
                          const est = getEventStyle(order);
                          const formattedDate = order.suggestedDate 
                            ? new Date(order.suggestedDate + 'T00:00:00').toLocaleDateString('pt-BR') 
                            : '';
                          return (
                            <div 
                              key={order.id} 
                              style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 10 }}
                            >
                              <div className="upcoming-os-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--sf-white)' }}>
                                  {order.id.toUpperCase().startsWith('OS-') ? order.id.toUpperCase() : `OS-${order.id.substring(0, 8)}`}
                                </span>
                                <span className="upcoming-os-status-badge" style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: est.style.background, color: est.style.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                  {order.type === 'ASSEMBLY' ? 'Montagem' : 'Assistência'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: 'var(--muted)' }}>
                                <div>Cliente: <span style={{ color: 'var(--sf-white)', fontWeight: 600 }}>{order.clientName.split(' ')[0]}</span></div>
                                <div>Data: <span style={{ color: 'var(--sf-white)' }}>{formattedDate}</span></div>
                                <div>Técnico: <span style={{ color: 'var(--sf-white)' }}>{getTechnicianName(order)}</span></div>
                                {order.environments && order.environments.length > 0 && (
                                  <div style={{ fontSize: 10, color: 'var(--cyan)', marginTop: 2 }}>
                                    {order.environments.map(e => e.name).join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Card Técnicos ativos */}
              <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, overflow:'hidden', marginBottom:14 }}>
                <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--border)', fontSize:12, fontWeight:600, color:'var(--text)', display:'flex', alignItems:'center', gap:6 }}>
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  Técnicos ativos
                </div>
                {isStarter ? (
                  <UpgradeLock onUpgrade={() => onChangeView('UPGRADE')} />
                ) : (
                  <div style={{ padding:'8px 14px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {activeTechs.length === 0 ? (
                        <p style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: '10px 0' }}>Nenhum técnico ativo este mês.</p>
                      ) : (
                        activeTechs.map(tech => (
                          <div 
                            key={tech.id} 
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 10 }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(26, 107, 255, 0.1)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <UserIcon size={12} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sf-white)' }}>{tech.name}</span>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--muted)', padding: '2px 8px', borderRadius: 12 }}>
                              {tech.count} O.S.
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'disponibilidade' && (
        <>
          {!isPro ? (
            <UpgradeRequired
              featureName="Disponibilidade de Técnicos"
              requiredPlan="Pro"
              onUpgrade={() => onChangeView('UPGRADE')}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Métricas */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                {/* Disponíveis agora */}
                <div style={{ background: 'var(--bg2)', border: '1px solid rgba(63,185,80,.2)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: -10, right: -10, width: 60, height: 60, borderRadius: '50%', background: 'rgba(63,185,80,.05)', filter: 'blur(10px)' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Disponíveis agora</span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--green)' }}>{disponiveisCount}</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>técnicos</span>
                  </div>
                </div>

                {/* Em andamento */}
                <div style={{ background: 'var(--bg2)', border: '1px solid rgba(163,113,247,.2)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: -10, right: -10, width: 60, height: 60, borderRadius: '50%', background: 'rgba(163,113,247,.05)', filter: 'blur(10px)' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Em andamento</span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--purple)' }}>{andamentoCount}</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>técnicos</span>
                  </div>
                </div>

                {/* Agendados */}
                <div style={{ background: 'var(--bg2)', border: '1px solid rgba(56,139,253,.2)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: -10, right: -10, width: 60, height: 60, borderRadius: '50%', background: 'rgba(56,139,253,.05)', filter: 'blur(10px)' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Agendados</span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--blue)' }}>{agendadoCount}</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>técnicos</span>
                  </div>
                </div>

                {/* Próxima liberação */}
                <div style={{ background: 'var(--bg2)', border: '1px solid rgba(227,160,8,.2)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', overflow: 'hidden', justifyContent: 'center' }}>
                  <div style={{ position: 'absolute', top: -10, right: -10, width: 60, height: 60, borderRadius: '50%', background: 'rgba(227,160,8,.05)', filter: 'blur(10px)' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Próxima liberação</span>
                  {proximaLiberacao ? (
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--amber)' }}>
                        {proximaLiberacao.date!.toLocaleDateString('pt-BR')}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', marginTop: 2 }}>
                        {proximaLiberacao.tech.name}
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 600 }}>Todos liberados</div>
                  )}
                </div>
              </div>

              {/* Timeline Wrapper */}
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, overflowX: 'auto' }}>
                <div style={{ minWidth: 800 }}>
                  {/* Header das datas da timeline */}
                  <div style={{ display: 'flex', paddingLeft: 222, marginBottom: 16, paddingRight: 16 }}>
                    <div style={{ flex: 1, position: 'relative', height: 16 }}>
                      {dateLabels.map((label, idx) => (
                        <div
                          key={idx}
                          style={{
                            position: 'absolute',
                            left: `${(idx / 10) * 100}%`,
                            transform: 'translateX(-50%)',
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'var(--muted)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {label}
                        </div>
                      ))}
                    </div>
                    {/* Espaço para alinhar com as ações */}
                    <div style={{ width: 140, flexShrink: 0 }} />
                  </div>

                  {/* Lista de técnicos */}
                  {sortedTechnicians.map(tech => {
                    const status = getTechStatus(tech.id);
                    const techOrders = getTechOrders(tech.id);
                    return (
                      <div
                        key={tech.id}
                        style={{
                          background: 'var(--bg2)',
                          border: `1px solid ${
                            status === 'disponivel' ? 'rgba(63,185,80,.25)' :
                            status === 'andamento' ? 'rgba(163,113,247,.25)' :
                            'rgba(56,139,253,.25)'
                          }`,
                          borderRadius: 10,
                          overflow: 'hidden',
                          marginBottom: 10,
                        }}
                      >
                        {/* Linha principal */}
                        <div
                          style={{ display:'flex', alignItems:'center', padding:'14px 16px', gap:12, cursor: techOrders.length ? 'pointer' : 'default' }}
                          onClick={() => techOrders.length && setExpandedTech(expandedTech === tech.id ? null : tech.id)}
                        >
                          {/* Avatar */}
                          <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,var(--blue),#0a3fa0)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#fff', flexShrink:0 }}>
                            {tech.name.split(' ').map(n => n[0]).slice(0,2).join('').toUpperCase()}
                          </div>

                          {/* Info do técnico */}
                          <div style={{ width:170, flexShrink:0 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{tech.name}</div>
                            <div style={{ fontSize:11, color:'var(--muted)', marginTop:1 }}>
                              {(tech as any).classification || tech.role}
                            </div>
                            <div style={{
                              fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:20,
                              border:'1px solid', marginTop:4, display:'inline-block',
                              ...(status === 'disponivel'
                                ? { background:'var(--green-dim)', borderColor:'var(--green-border)', color:'var(--green)' }
                                : status === 'andamento'
                                ? { background:'var(--purple-dim)', borderColor:'var(--purple-border)', color:'var(--purple)' }
                                : { background:'var(--blue-dim)', borderColor:'var(--blue-border)', color:'var(--blue)' }
                              )
                            }}>
                              {status === 'disponivel' ? 'Disponível' : status === 'andamento' ? 'Em andamento' : 'Agendado'}
                            </div>
                          </div>

                          {/* Timeline */}
                          <div style={{ flex:1, position:'relative', height:48, display:'flex', alignItems:'center' }}>
                            {/* Listras de fundo */}
                            <div style={{ position:'absolute', inset:0, display:'flex' }}>
                              {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} style={{ flex:1, borderRight: i < 9 ? '1px solid var(--border)' : 'none' }} />
                              ))}
                            </div>

                            {/* Linha do hoje */}
                            <div style={{ position:'absolute', top:0, bottom:0, width:2, background:'rgba(248,81,73,.6)', left:todayPosition, zIndex:2 }} />

                            {/* Técnico disponível */}
                            {status === 'disponivel' && (
                              <div style={{ position:'relative', zIndex:3, paddingLeft:8, fontSize:13, color:'var(--green)', display:'flex', alignItems:'center', gap:6 }}>
                                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                                Disponível para nova O.S.
                              </div>
                            )}

                            {/* Barras das O.S. */}
                            {techOrders.map(order => {
                              const pos = getBarPosition(order);
                              const isInProgress = order.status === 'IN_PROGRESS';
                              const isAssistance = order.type === 'ASSISTANCE';
                              return (
                                <div
                                  key={order.id}
                                  style={{
                                    position:'absolute',
                                    height:28, borderRadius:6,
                                    display:'flex', alignItems:'center',
                                    padding:'0 8px', fontSize:11, fontWeight:500,
                                    cursor:'pointer', overflow:'hidden', whiteSpace:'nowrap', zIndex:3,
                                    left: pos.left, width: pos.width,
                                    ...(isInProgress && !isAssistance
                                      ? { background:'rgba(56,139,253,.25)', border:'1px solid rgba(56,139,253,.4)', color:'#7bb8ff' }
                                      : isAssistance
                                      ? { background:'rgba(227,160,8,.2)', border:'1px solid rgba(227,160,8,.35)', color:'#f5cc6a' }
                                      : { background:'rgba(56,139,253,.1)', border:'1px dashed rgba(56,139,253,.35)', color:'var(--blue)' }
                                    )
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedOrderForModal(order);
                                  }}
                                >
                                  {order.id}
                                </div>
                              );
                            })}
                          </div>

                          {/* Ações */}
                          <div style={{ display:'flex', alignItems:'center', gap:6, marginLeft:12, flexShrink:0, width: 140, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                            {status !== 'disponivel' && (
                              <button
                                onClick={() => onSelectOrder && onSelectOrder(techOrders[0])}
                                style={{ background:'var(--blue-dim)', border:'1px solid var(--blue-border)', borderRadius:7, color:'var(--blue)', fontSize:12, padding:'5px 10px', cursor:'pointer', display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap' }}
                              >
                                <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>
                                Ver O.S.
                              </button>
                            )}
                            {status === 'disponivel' && (
                              <button
                                onClick={() => onChangeView('NEW')}
                                style={{ background:'var(--green-dim)', border:'1px solid var(--green-border)', borderRadius:7, color:'var(--green)', fontSize:12, padding:'5px 10px', cursor:'pointer', display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap' }}
                              >
                                <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                Nova O.S.
                              </button>
                            )}
                            {techOrders.length > 0 && (
                              <button
                                onClick={() => setExpandedTech(expandedTech === tech.id ? null : tech.id)}
                                style={{ background:'transparent', border:'1px solid var(--border)', borderRadius:7, color:'var(--muted)', fontSize:12, padding:'5px 8px', cursor:'pointer' }}
                              >
                                {expandedTech === tech.id ? '▴' : '▾'}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Detalhe expandido */}
                        {expandedTech === tech.id && techOrders.length > 0 && (
                          <div style={{ borderTop:'1px solid var(--border)', padding:'12px 16px', background:'rgba(255,255,255,.01)' }}>
                            {techOrders.map(order => (
                              <div key={order.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                                <span style={{
                                  fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20,
                                  fontFamily:'DM Mono,monospace', flexShrink:0,
                                  background: order.status === 'IN_PROGRESS' ? 'var(--purple-dim)' : 'var(--blue-dim)',
                                  border: `1px solid ${order.status === 'IN_PROGRESS' ? 'var(--purple-border)' : 'var(--blue-border)'}`,
                                  color: order.status === 'IN_PROGRESS' ? 'var(--purple)' : 'var(--blue)',
                                }}>
                                  {order.id}
                                </span>
                                <div style={{ flex:1 }}>
                                  <div style={{ fontSize:12, fontWeight:500, color:'var(--text)' }}>{order.clientName}</div>
                                  <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:4 }}>
                                    {order.environments?.filter(e => e.technicianId === tech.id).map(env => (
                                      <span key={env.id} style={{ fontSize:11, padding:'2px 7px', borderRadius:20, background:'var(--bg4)', border:'1px solid var(--border)', color:'var(--muted)' }}>
                                        {env.name} · {env.estimatedDays}d
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <div style={{ textAlign:'right', fontSize:11, color:'var(--muted)', flexShrink:0 }}>
                                  <div>Início: {order.suggestedDate ? new Date(order.suggestedDate + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</div>
                                  <div style={{ color:'var(--amber)', fontWeight:500 }}>
                                    Prazo: {getOrderEndDateObj(order).toLocaleDateString('pt-BR')}
                                  </div>
                                </div>
                              </div>
                            ))}
                            {getNextAvailable(tech.id) && (
                              <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid var(--border)', fontSize:12, color:'var(--muted)' }}>
                                🕐 Disponível a partir de{' '}
                                <strong style={{ color:'var(--text)' }}>
                                  {getNextAvailable(tech.id)!.toLocaleDateString('pt-BR')}
                                </strong>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Legenda */}
                  <div style={{ display:'flex', gap:16, marginTop:16, flexWrap:'wrap' }}>
                    {[
                      { color:'rgba(56,139,253,.25)', border:'1px solid rgba(56,139,253,.4)', label:'Montagem em andamento' },
                      { color:'rgba(227,160,8,.2)', border:'1px solid rgba(227,160,8,.35)', label:'Assistência' },
                      { color:'rgba(56,139,253,.1)', border:'1px dashed rgba(56,139,253,.35)', label:'Agendado (futuro)' },
                    ].map(item => (
                      <div key={item.label} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--muted)' }}>
                        <div style={{ width:16, height:10, borderRadius:3, background:item.color, border:item.border }} />
                        {item.label}
                      </div>
                    ))}
                    <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--muted)' }}>
                      <div style={{ width:2, height:14, background:'rgba(248,81,73,.6)' }} />
                      Hoje
                    </div>
                  </div>

                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal: List of multiple O.S. on a day */}
      {selectedDayOrders && selectedDayDate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }}>
          <div className="animate-fade-in" style={{ background: 'var(--navy3)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 16, width: '100%', maxWidth: 480, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.6)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--navy2)' }}>
              <h3 style={{ fontWeight: 700, color: 'var(--sf-white)', fontSize: 16 }}>
                O.S. em {selectedDayDate.toLocaleDateString('pt-BR')}
              </h3>
              <button 
                onClick={() => { setSelectedDayOrders(null); setSelectedDayDate(null); }} 
                style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '60vh', overflowY: 'auto' }}>
              {selectedDayOrders.map(order => {
                const est = getEventStyle(order);
                return (
                  <div 
                    key={order.id}
                    onClick={() => handleSelectOrderFromList(order)}
                    style={{
                      padding: 14,
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'border-color 0.2s',
                    }}
                    className="hover:border-white/20"
                  >
                    <div>
                      <div style={{ display: 'flex', items: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--sf-white)' }}>{order.id}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: est.style.background, color: est.style.color }}>
                          {order.type === 'ASSEMBLY' ? 'Montagem' : 'Assistência'}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                        Cliente: <span style={{ color: 'var(--sf-white)' }}>{order.clientName}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        Técnico: {getTechnicianName(order)}
                      </div>
                    </div>
                    <ChevronRight size={18} style={{ color: 'var(--muted)' }} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modal: O.S. Details from calendar event click */}
      {selectedOrderForModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }}>
          <div className="animate-fade-in" style={{ background: 'var(--navy3)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 16, width: '100%', maxWidth: 540, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.6)' }}>
            
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--navy2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span 
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 4,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                    background: selectedOrderForModal.type === 'ASSEMBLY' ? 'rgba(56,139,253,.2)' : 'rgba(227,160,8,.2)',
                    color: selectedOrderForModal.type === 'ASSEMBLY' ? '#7bb8ff' : '#f5cc6a',
                    border: selectedOrderForModal.type === 'ASSEMBLY' ? '1px solid rgba(56,139,253,.3)' : '1px solid rgba(227,160,8,.3)'
                  }}
                >
                  {selectedOrderForModal.type === 'ASSEMBLY' ? 'Montagem' : 'Assistência'}
                </span>
                <h3 style={{ fontWeight: 700, color: 'var(--sf-white)', fontSize: 16 }}>
                  {selectedOrderForModal.id}
                </h3>
              </div>
              <button 
                onClick={() => setSelectedOrderForModal(null)} 
                style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '70vh', overflowY: 'auto' }}>
              
              {/* Primary Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Cliente</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sf-white)' }}>{selectedOrderForModal.clientName}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{selectedOrderForModal.clientPhone}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Status</div>
                  <div style={{ 
                    display: 'inline-block',
                    padding: '2px 8px', 
                    borderRadius: 999, 
                    fontSize: 11, 
                    fontWeight: 700, 
                    textTransform: 'uppercase',
                    background: getStatusColor(selectedOrderForModal.status).replace('text-', 'bg-').replace('800', '100') + '15',
                    color: getStatusColor(selectedOrderForModal.status).includes('green') ? '#34d399' : getStatusColor(selectedOrderForModal.status).includes('blue') ? '#60a5fa' : '#fbbf24'
                  }}>
                    {getStatusLabel(selectedOrderForModal.status)}
                  </div>
                </div>
              </div>

              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Datas</div>
                <div style={{ fontSize: 12, color: 'var(--sf-white)' }}>
                  Início: <span style={{ fontWeight: 600 }}>{selectedOrderForModal.suggestedDate ? new Date(selectedOrderForModal.suggestedDate + 'T00:00:00').toLocaleDateString('pt-BR') : 'N/A'}</span>
                </div>
                {(() => {
                  const events = (selectedOrderForModal as any).events || (selectedOrderForModal as any).workOrderEvents || [];
                  const hasDelay = events.some((e: any) => e.impactDeadline && (e.impactWorkdays || 0) > 0);
                  return (
                    <div style={{ fontSize: 12, color: 'var(--sf-white)', marginTop: 2 }}>
                      Previsão de término:{' '}
                      <span style={{ fontWeight: 600, color: hasDelay ? 'var(--red)' : 'inherit' }}>
                        {getOrderEndDate(selectedOrderForModal).toLocaleDateString('pt-BR')}
                        {hasDelay && <span style={{ color: 'var(--red)', fontSize: 11 }}> (com atraso)</span>}
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Address */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Endereço</div>
                <div style={{ fontSize: 13, color: 'var(--sf-white)', display: 'flex', alignItems: 'start', gap: 6, lineHeight: 1.4 }}>
                  <MapPin size={14} style={{ color: 'var(--muted)', flexShrink: 0, marginTop: 2 }} />
                  {selectedOrderForModal.address}
                </div>
              </div>

              {/* Environments list with per-env technician */}
              {selectedOrderForModal.environments && selectedOrderForModal.environments.length > 0 ? (
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Ambientes</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {selectedOrderForModal.environments.map((env: Environment) => (
                      <div
                        key={env.id}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          background: 'rgba(255,255,255,0.02)',
                          border: '1px solid var(--border)',
                          borderRadius: 8,
                          padding: '8px 12px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sf-white)' }}>{env.name}</span>
                          {env.estimatedDays && (
                            <span style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'monospace' }}>
                              {env.estimatedDays}d
                            </span>
                          )}
                        </div>
                        <span style={{
                          fontSize: 12,
                          color: env.technicianName ? 'var(--blue)' : 'var(--muted)',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          <UserIcon size={11} />
                          {env.technicianName || 'Não atribuído'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* Fallback para O.S. sem ambientes (ex.: assistência) */
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Técnico Responsável</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--sf-white)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <UserIcon size={14} style={{ color: 'var(--muted)' }} />
                    {getTechnicianName(selectedOrderForModal)}
                  </div>
                </div>
              )}

            </div>

            {/* Actions Footer */}
            <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,.08)', display: 'flex', gap: 12, background: 'var(--navy2)' }}>
              <button 
                onClick={() => setSelectedOrderForModal(null)} 
                style={{ flex: 1, height: 40, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--sf-white)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Fechar
              </button>
              {onSelectOrder && (
                <button 
                  onClick={() => {
                    if (onSelectOrder) {
                      onSelectOrder(selectedOrderForModal);
                    }
                    setSelectedOrderForModal(null);
                  }}
                  style={{ flex: 1, height: 40, background: 'var(--blue)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', boxShadow: '0 4px 12px rgba(26,107,255,0.2)' }}
                >
                  Abrir O.S.
                </button>
              )}
            </div>

          </div>
        </div>
      )}

    </div>
  );
};
